import React, { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import _ from 'lodash';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';

import { selectIsClaimAllowed } from 'store/auth/selectors';

import CicChangesTable from './components/CicChangesTable';
import CicEarlierReasons from './components/CicEarlierReasons';
import CicFeeChangesTable from './components/CicFeeChangesTable';
import CicPendingCard from './components/CicPendingCard';
import CicSectionCard from './components/CicSectionCard';
import CicSentDisclosures from './components/CicSentDisclosures';
import { useLoanCicHistory } from './hooks/useLoanCicHistory';
import { useOpenCicEditHistoryModal } from './hooks/useOpenCicEditHistoryModal';
import { useOpenCicEditReasonModal } from './hooks/useOpenCicEditReasonModal';
import {
  CIC_HISTORY_DESCRIPTION,
  CIC_HISTORY_TITLE,
  EMPTY_HISTORY_MESSAGE,
  LOAD_ERROR_MESSAGE,
  NO_ITEMS,
  RETRY_BUTTON_LABEL,
  UNATTACHED_FEE_CHANGES_DESCRIPTION,
  UNATTACHED_FEE_CHANGES_TITLE,
  UNATTACHED_REMOVED_DESCRIPTION,
  UNATTACHED_REMOVED_TITLE,
} from './LoanCicHistory.constants';
import { headerStyles, rootStyles, stateStyles } from './LoanCicHistory.styles';
import { LoanCicHistoryTestIds } from './LoanCicHistory.testIds';
import { getIsHistoryEmpty } from './LoanCicHistory.utils';

const LoanCicHistory = () => {
  const isEditAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_EDITOR_EDIT_CIC_REASONS),
  );

  const { history, isLoading, isError, reload } = useLoanCicHistory();

  const openEditReasonModal = useOpenCicEditReasonModal({ onHistoryChanged: reload });
  const onOpenHistory = useOpenCicEditHistoryModal();

  const pending = history?.pending ?? null;
  const disclosures = history?.disclosures ?? NO_ITEMS;
  const removed = history?.removed ?? NO_ITEMS;
  const earlier = history?.earlier ?? NO_ITEMS;
  const feeChanges = history?.feeChanges ?? NO_ITEMS;

  const isInitialLoading = isLoading && _.isNil(history);
  const isLoadFailed = isError && _.isNil(history);
  const isEmpty = !_.isNil(history) && getIsHistoryEmpty(history);

  const onEdit = useCallback(
    change =>
      openEditReasonModal({
        change,
        bundleType: pending?.bundleType ?? null,
        plannedTriggerDate: pending?.plannedTriggerDate ?? null,
      }),
    [openEditReasonModal, pending],
  );

  const onEditFeeChange = useCallback(
    feeChange => {
      const isPendingFeeChange = _.some(pending?.feeChanges, { id: feeChange.id });

      openEditReasonModal({
        change: feeChange,
        isFeeChange: true,
        bundleType: isPendingFeeChange ? pending.bundleType : null,
        plannedTriggerDate: isPendingFeeChange ? pending.plannedTriggerDate : null,
      });
    },
    [openEditReasonModal, pending],
  );

  return (
    <Box data-testid={LoanCicHistoryTestIds.TAB} sx={rootStyles}>
      <Box sx={headerStyles}>
        <Typography variant="bold20">{CIC_HISTORY_TITLE}</Typography>
        <Typography color="text.secondary" variant="regular12">
          {CIC_HISTORY_DESCRIPTION}
        </Typography>
      </Box>

      {isInitialLoading && (
        <Box sx={stateStyles}>
          <CircularProgress data-testid={LoanCicHistoryTestIds.LOADING_SPINNER} size={28} />
        </Box>
      )}

      {isLoadFailed && !isLoading && (
        <Box sx={stateStyles}>
          <Typography
            color="text.secondary"
            data-testid={LoanCicHistoryTestIds.LOAD_ERROR_LABEL}
            variant="regular14"
          >
            {LOAD_ERROR_MESSAGE}
          </Typography>
          <Button data-testid={LoanCicHistoryTestIds.RETRY_BUTTON} variant="line" onClick={reload}>
            {RETRY_BUTTON_LABEL}
          </Button>
        </Box>
      )}

      {isEmpty && (
        <Box sx={stateStyles}>
          <Typography
            color="text.secondary"
            data-testid={LoanCicHistoryTestIds.EMPTY_LABEL}
            variant="regular14"
          >
            {EMPTY_HISTORY_MESSAGE}
          </Typography>
        </Box>
      )}

      {!!pending && (
        <CicPendingCard
          isEditAllowed={isEditAllowed}
          pending={pending}
          onEdit={onEdit}
          onEditFeeChange={onEditFeeChange}
          onOpenHistory={onOpenHistory}
        />
      )}

      {!_.isNil(history) && !isEmpty && (
        <CicSentDisclosures
          disclosures={disclosures}
          isEditAllowed={isEditAllowed}
          onEditFeeChange={onEditFeeChange}
          onOpenHistory={onOpenHistory}
        >
          {!_.isEmpty(earlier) && <CicEarlierReasons earlier={earlier} />}
        </CicSentDisclosures>
      )}

      {!_.isEmpty(removed) && (
        <CicSectionCard
          dataTestId={LoanCicHistoryTestIds.UNATTACHED_REMOVED_CARD}
          description={UNATTACHED_REMOVED_DESCRIPTION}
          title={UNATTACHED_REMOVED_TITLE}
        >
          <CicChangesTable changes={removed} onOpenHistory={onOpenHistory} />
        </CicSectionCard>
      )}

      {!_.isEmpty(feeChanges) && (
        <CicSectionCard
          dataTestId={LoanCicHistoryTestIds.UNATTACHED_FEE_CHANGES_CARD}
          description={UNATTACHED_FEE_CHANGES_DESCRIPTION}
          title={UNATTACHED_FEE_CHANGES_TITLE}
        >
          <CicFeeChangesTable
            feeChanges={feeChanges}
            isEditAllowed={isEditAllowed}
            onEdit={onEditFeeChange}
          />
        </CicSectionCard>
      )}
    </Box>
  );
};

export default memo(LoanCicHistory);
